import { useMemo, useState } from 'react'
import { Search, X, Copy, FileText, Sparkles, Sun } from 'lucide-react'
import toast from 'react-hot-toast'
import { copiarTexto } from '@/utils/clipboard'
import {
  OLEOS_PROTOCOLO,
  buscarOleos,
  gerarTextoProtocolo,
  type OleoProtocolo,
} from '@/utils/protocolosEssenciais'
import {
  montarRotinaDiaria,
  gerarTextoRotinaDiaria,
  OBSERVACAO_ROTINA_DIARIA,
  type SecaoRotina,
} from '@/utils/rotinaDiariaProtocolos'

const VERDE = '#2D6A4F'
const VERDE_ESCURO = '#1D5C3A'
const DOURADO = '#C9A84C'
const BEGE_BORDA = '#EDE0D4'

export default function AbaProtocolosOleosEssenciais() {
  const [termo, setTermo] = useState('')
  const [selecionados, setSelecionados] = useState<string[]>([])
  const [nomeCliente, setNomeCliente] = useState('')
  const [gerado, setGerado] = useState(false)
  const [rotinaGerada, setRotinaGerada] = useState(false)

  const resultados = useMemo(() => buscarOleos(termo), [termo])

  const oleosSelecionados = useMemo(
    () =>
      selecionados
        .map((id) => OLEOS_PROTOCOLO.find((o) => o.id === id))
        .filter((o): o is OleoProtocolo => Boolean(o)),
    [selecionados]
  )

  const rotina = useMemo<SecaoRotina[]>(
    () => (rotinaGerada ? montarRotinaDiaria(oleosSelecionados) : []),
    [rotinaGerada, oleosSelecionados]
  )

  const jaSelecionado = (id: string) => selecionados.includes(id)

  const alternarSelecao = (id: string) => {
    setGerado(false)
    setRotinaGerada(false)
    setSelecionados((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    )
  }

  const remover = (id: string) => {
    setGerado(false)
    setRotinaGerada(false)
    setSelecionados((prev) => prev.filter((x) => x !== id))
  }

  const gerar = () => {
    if (oleosSelecionados.length === 0) {
      toast.error('Selecione ao menos um óleo para gerar o protocolo.')
      return
    }
    setGerado(true)
  }

  const gerarRotina = () => {
    if (oleosSelecionados.length === 0) {
      toast.error('Selecione ao menos um óleo para gerar a rotina diária.')
      return
    }
    setRotinaGerada(true)
  }

  const copiar = async () => {
    try {
      await copiarTexto(gerarTextoProtocolo(oleosSelecionados, nomeCliente))
      toast.success('Protocolo copiado!')
    } catch {
      toast.error('Não foi possível copiar o protocolo.')
    }
  }

  const copiarRotina = async () => {
    try {
      await copiarTexto(gerarTextoRotinaDiaria(rotina, nomeCliente))
      toast.success('Rotina copiada!')
    } catch {
      toast.error('Não foi possível copiar a rotina.')
    }
  }

  return (
    <div className="space-y-6">
      {/* Cabeçalho */}
      <div
        className="rounded-2xl p-6"
        style={{ backgroundColor: VERDE_ESCURO, color: '#FFFFFF' }}
      >
        <div className="flex items-start gap-3">
          <div
            className="rounded-full p-2 flex-shrink-0"
            style={{ backgroundColor: 'rgba(255,255,255,0.14)' }}
          >
            <FileText size={20} />
          </div>
          <div>
            <h2 className="font-cormorant text-2xl font-semibold leading-tight">
              Protocolos de Óleos Essenciais
            </h2>
            <p className="text-sm mt-1" style={{ color: 'rgba(255,255,255,0.82)' }}>
              Busque os óleos, selecione e gere o protocolo com as fichas oficiais do sistema.
            </p>
          </div>
        </div>
      </div>

      {/* Busca */}
      <div
        className="rounded-2xl p-5 space-y-4"
        style={{ backgroundColor: '#FFFFFF', border: `1px solid ${BEGE_BORDA}` }}
      >
        <label className="block text-sm font-medium" style={{ color: '#2C2C2A' }}>
          Buscar óleo
        </label>
        <div className="relative">
          <Search
            size={18}
            className="absolute left-4 top-1/2 -translate-y-1/2"
            style={{ color: '#B08968' }}
          />
          <input
            type="text"
            value={termo}
            onChange={(e) => setTermo(e.target.value)}
            placeholder="Digite o nome do óleo (ex.: lavanda, on guard, calmer...)"
            className="w-full pl-11 pr-10 py-3 rounded-full text-sm outline-none focus:ring-2"
            style={{
              border: `1px solid ${BEGE_BORDA}`,
              backgroundColor: '#F8F4EF',
              color: '#2C2C2A',
            }}
          />
          {termo && (
            <button
              type="button"
              onClick={() => setTermo('')}
              aria-label="Limpar busca"
              className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1 hover:bg-black/5"
            >
              <X size={16} style={{ color: '#B08968' }} />
            </button>
          )}
        </div>

        {termo.trim() === '' && (
          <p className="text-xs" style={{ color: '#5F5E5A' }}>
            {OLEOS_PROTOCOLO.length} óleos disponíveis com ficha cadastrada.
          </p>
        )}

        {termo.trim() !== '' && resultados.length === 0 && (
          <p className="text-sm" style={{ color: '#5F5E5A' }}>
            Nenhum óleo encontrado para “{termo}”.
          </p>
        )}

        {resultados.length > 0 && (
          <ul className="space-y-2 max-h-72 overflow-y-auto pr-1">
            {resultados.map((oleo) => {
              const ativo = jaSelecionado(oleo.id)
              return (
                <li key={oleo.id}>
                  <button
                    type="button"
                    onClick={() => alternarSelecao(oleo.id)}
                    aria-pressed={ativo}
                    className="w-full text-left px-4 py-3 rounded-xl flex items-center justify-between gap-3 transition-colors"
                    style={{
                      backgroundColor: ativo ? '#D8F3DC' : '#FFFFFF',
                      border: `1px solid ${ativo ? VERDE : BEGE_BORDA}`,
                    }}
                  >
                    <span className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium" style={{ color: '#2C2C2A' }}>
                        {oleo.lista}
                      </span>
                      {oleo.etiqueta && (
                        <span
                          className="text-[10px] px-2 py-0.5 rounded-full font-medium"
                          style={{ backgroundColor: '#F8F4EF', color: '#B08968', border: `1px solid ${BEGE_BORDA}` }}
                        >
                          {oleo.etiqueta}
                        </span>
                      )}
                    </span>
                    <span className="text-xs font-medium flex-shrink-0" style={{ color: ativo ? VERDE : '#B08968' }}>
                      {ativo ? 'Selecionado' : 'Adicionar'}
                    </span>
                  </button>
                </li>
              )
            })}
          </ul>
        )}
      </div>

      {/* Selecionados */}
      <div
        className="rounded-2xl p-5 space-y-4"
        style={{ backgroundColor: '#FFFFFF', border: `1px solid ${BEGE_BORDA}` }}
      >
        <div className="flex items-center justify-between">
          <h3 className="font-cormorant text-lg font-semibold" style={{ color: VERDE_ESCURO }}>
            Óleos selecionados
          </h3>
          <span className="text-xs" style={{ color: '#5F5E5A' }}>
            {oleosSelecionados.length} selecionado{oleosSelecionados.length === 1 ? '' : 's'}
          </span>
        </div>

        {oleosSelecionados.length === 0 ? (
          <p className="text-sm" style={{ color: '#5F5E5A' }}>
            Nenhum óleo selecionado ainda. Use a busca acima para adicionar.
          </p>
        ) : (
          <ul className="space-y-2">
            {oleosSelecionados.map((oleo) => (
              <li
                key={oleo.id}
                className="flex items-center justify-between gap-3 px-4 py-3 rounded-xl"
                style={{ backgroundColor: '#F8F4EF', border: `1px solid ${BEGE_BORDA}` }}
              >
                <span className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium" style={{ color: '#2C2C2A' }}>
                    {oleo.lista}
                  </span>
                  {oleo.etiqueta && (
                    <span
                      className="text-[10px] px-2 py-0.5 rounded-full font-medium"
                      style={{ backgroundColor: '#FFFFFF', color: '#B08968', border: `1px solid ${BEGE_BORDA}` }}
                    >
                      {oleo.etiqueta}
                    </span>
                  )}
                </span>
                <button
                  type="button"
                  onClick={() => remover(oleo.id)}
                  aria-label={`Remover ${oleo.lista}`}
                  className="rounded-full p-1.5 hover:bg-black/5 flex-shrink-0"
                >
                  <X size={16} style={{ color: '#B08968' }} />
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="pt-1">
          <label className="block text-sm font-medium mb-2" style={{ color: '#2C2C2A' }}>
            Nome da cliente <span className="font-normal" style={{ color: '#5F5E5A' }}>(opcional)</span>
          </label>
          <input
            type="text"
            value={nomeCliente}
            onChange={(e) => setNomeCliente(e.target.value)}
            placeholder="Aparece no topo do protocolo"
            className="w-full px-4 py-3 rounded-xl text-sm outline-none focus:ring-2"
            style={{ border: `1px solid ${BEGE_BORDA}`, backgroundColor: '#F8F4EF', color: '#2C2C2A' }}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <button
            type="button"
            onClick={gerar}
            className="w-full py-3 rounded-full text-sm font-medium text-white flex items-center justify-center gap-2 transition-opacity hover:opacity-90"
            style={{ backgroundColor: VERDE }}
          >
            <Sparkles size={16} />
            Gerar protocolo
          </button>
          <button
            type="button"
            onClick={gerarRotina}
            className="w-full py-3 rounded-full text-sm font-medium flex items-center justify-center gap-2 transition-opacity hover:opacity-90"
            style={{ backgroundColor: '#FFFFFF', color: VERDE, border: `1px solid ${VERDE}` }}
          >
            <Sun size={16} />
            Gerar rotina diária
          </button>
        </div>
      </div>

      {/* Resultado — rotina diária */}
      {rotinaGerada && oleosSelecionados.length > 0 && (
        <div
          className="rounded-2xl p-5 space-y-4"
          style={{ backgroundColor: '#FFFFFF', border: `1px solid ${BEGE_BORDA}` }}
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <h3 className="font-cormorant text-xl font-semibold" style={{ color: VERDE_ESCURO }}>
              {nomeCliente.trim()
                ? `Rotina diária de ${nomeCliente.trim()}`
                : 'Rotina diária'}
            </h3>
            <button
              type="button"
              onClick={copiarRotina}
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium text-white transition-opacity hover:opacity-90"
              style={{ backgroundColor: VERDE }}
            >
              <Copy size={15} />
              Copiar para WhatsApp
            </button>
          </div>

          <div className="space-y-5">
            {rotina.map((secao) => (
              <div key={secao.titulo} className="space-y-3">
                {rotina.length > 1 && (
                  <h4 className="font-cormorant text-lg font-semibold" style={{ color: DOURADO }}>
                    {secao.titulo}
                  </h4>
                )}
                {secao.blocos.map((bloco) => (
                  <div
                    key={`${secao.titulo}-${bloco.periodo}`}
                    className="rounded-xl p-4"
                    style={{ backgroundColor: '#F8F4EF', border: `1px solid ${BEGE_BORDA}` }}
                  >
                    <h5
                      className="text-xs font-semibold uppercase tracking-wide mb-2"
                      style={{ color: VERDE }}
                    >
                      {bloco.titulo}
                    </h5>
                    <ul className="space-y-1.5">
                      {bloco.itens.map((item, i) => (
                        <li key={`${item.nome}-${i}`} className="text-sm leading-relaxed" style={{ color: '#2C2C2A' }}>
                          <span className="font-semibold">{item.nome}:</span> {item.texto}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            ))}
          </div>

          <p
            className="text-sm leading-relaxed rounded-xl p-4"
            style={{
              backgroundColor: '#F8F4EF',
              color: '#5F5E5A',
              border: `1px solid ${BEGE_BORDA}`,
            }}
          >
            {OBSERVACAO_ROTINA_DIARIA}
          </p>

          <button
            type="button"
            onClick={copiarRotina}
            className="w-full py-3 rounded-full text-sm font-medium text-white flex items-center justify-center gap-2 transition-opacity hover:opacity-90"
            style={{ backgroundColor: VERDE }}
          >
            <Copy size={16} />
            Copiar para WhatsApp
          </button>
        </div>
      )}

      {/* Resultado */}
      {gerado && oleosSelecionados.length > 0 && (
        <div
          className="rounded-2xl p-5 space-y-4"
          style={{ backgroundColor: '#FFFFFF', border: `1px solid ${BEGE_BORDA}` }}
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <h3 className="font-cormorant text-xl font-semibold" style={{ color: VERDE_ESCURO }}>
              {nomeCliente.trim()
                ? `Protocolo de ${nomeCliente.trim()}`
                : 'Protocolo gerado'}
            </h3>
            <button
              type="button"
              onClick={copiar}
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium text-white transition-opacity hover:opacity-90"
              style={{ backgroundColor: VERDE }}
            >
              <Copy size={15} />
              Copiar para WhatsApp
            </button>
          </div>

          <div className="space-y-4">
            {oleosSelecionados.map((oleo) => (
              <div
                key={oleo.id}
                className="rounded-xl p-4 space-y-2"
                style={{ backgroundColor: '#F8F4EF', border: `1px solid ${BEGE_BORDA}` }}
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <h4 className="font-cormorant text-lg font-semibold" style={{ color: VERDE_ESCURO }}>
                    {oleo.nome}
                  </h4>
                  {oleo.etiqueta && (
                    <span
                      className="text-[10px] px-2 py-0.5 rounded-full font-medium"
                      style={{ backgroundColor: '#FFFFFF', color: '#B08968', border: `1px solid ${BEGE_BORDA}` }}
                    >
                      {oleo.etiqueta}
                    </span>
                  )}
                </div>

                <dl className="space-y-2">
                  {oleo.campos.map((campo, i) => {
                    const ehAtencao = campo.rotulo === 'Atenção'
                    return (
                      <div key={`${oleo.id}-${i}`}>
                        <dt
                          className="text-xs font-semibold uppercase tracking-wide"
                          style={{ color: ehAtencao ? DOURADO : '#B08968' }}
                        >
                          {campo.rotulo}
                        </dt>
                        <dd
                          className="text-sm leading-relaxed whitespace-pre-line"
                          style={{ color: '#2C2C2A' }}
                        >
                          {campo.texto}
                        </dd>
                      </div>
                    )
                  })}
                </dl>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
